import { useSelector } from 'react-redux';
import { Button, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { BorderColor, PushPin, Save } from '@mui/icons-material';
import useAddressInfo from '../hooks/useAddressInfo';
import { H4, Body2 } from '../../../../../themes/typography';

function AddressInfo() {
    const { user } = useSelector((state) => state.auth);
    const { formik, countries, loading, allCities, allStates } = useAddressInfo();

    return (
        <div className="invoice-info-main-container">
            <form id="personal-info-form" onSubmit={formik.handleSubmit} noValidate>
                <div className="invoice-info-header">
                    <div className="invoice-info-title">
                        <span className="invoice-info-icon">
                            <PushPin color="secondary" sx={{ fontSize: '1.75rem' }} />
                        </span>
                        <H4 fontWeight={600}>Address Information</H4>
                    </div>
                    <div className="invoice-info-edit-button">
                        <Button
                            variant="text"
                            type="button"
                            startIcon={
                                loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : formik.values.isEditable ? (
                                    <Save />
                                ) : (
                                    <BorderColor />
                                )
                            }
                            disableRipple
                            onClick={() => {
                                if (formik.values.isEditable) {
                                    formik.handleSubmit();
                                } else {
                                    formik.setFieldValue('isEditable', true);
                                }
                            }}
                            disabled={loading}
                        >
                            {formik.values.isEditable ? (loading ? 'Saving' : 'Save') : 'Edit'}
                        </Button>
                    </div>
                </div>
                <div className="invoice-info-body">
                    <div className="invoice-info-details-preview">
                        <div className="user-address-preview">
                            <div className="user-address-fields">

                                {user?.address && (
                                    <Body2 fontWeight={400}>
                                        {user?.address}
                                    </Body2>
                                )}

                                {user?.address_2 && (
                                    <Body2 fontWeight={400}>
                                        {user?.address_2}
                                    </Body2>
                                )}

                            </div>
                            <div className="user-address-fields">

                                {user?.invoice_city_id && (
                                    <Body2 fontWeight={400}>
                                        {allCities.find((city) => city.id === user?.invoice_city_id)?.name}
                                    </Body2>
                                )}

                                {user?.state_id && (
                                    <Body2 fontWeight={400}>
                                        {allStates.find((state) => state.id === user?.state_id)?.name}
                                    </Body2>
                                )}

                                {user?.pincode && (
                                    <Body2 fontWeight={400}>
                                        {user?.pincode}
                                    </Body2>
                                )}
                            </div>

                            {user?.country_id && (
                                <div className="user-address-fields">
                                    <Body2 fontWeight={400}>
                                        {countries.find((country) => country.id === user?.country_id)?.name}
                                    </Body2>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="invoice-info-details">
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <TextField
                                    required
                                    fullWidth
                                    label="Address Line 1"
                                    name="addressLine1"
                                    id="addressLine1"
                                    value={formik.values.addressLine1}
                                    disabled={!formik.values.isEditable}
                                    onChange={(e) =>
                                        formik.setFieldValue('addressLine1', e.target.value)
                                    }
                                    error={
                                        formik.touched.addressLine1 &&
                                        Boolean(formik.errors.addressLine1)
                                    }
                                    helperText={
                                        formik.touched.addressLine1 && formik.errors.addressLine1
                                    }
                                />
                            </div>
                            <div className="invoice-info-input-field">
                                <TextField
                                    fullWidth
                                    label="Address Line 2 (Optional)"
                                    name="addressLine2"
                                    id="addressLine2"
                                    value={formik.values.addressLine2}
                                    disabled={!formik.values.isEditable}
                                    onChange={(e) =>
                                        formik.setFieldValue('addressLine2', e.target.value)
                                    }
                                    error={
                                        formik.touched.addressLine2 &&
                                        Boolean(formik.errors.addressLine2)
                                    }
                                    helperText={
                                        formik.touched.addressLine2 && formik.errors.addressLine2
                                    }
                                />
                            </div>
                        </div>
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <Autocomplete
                                    fullWidth
                                    id="city"
                                    name="city"
                                    options={allCities}
                                    disabled={!formik.values.isEditable}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) =>
                                        formik.setFieldValue('city', newValue)
                                    }
                                    value={formik.values.city}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="City"
                                            error={
                                                formik.touched.city && Boolean(formik.errors.city)
                                            }
                                            helperText={formik.touched.city && formik.errors.city}
                                        />
                                    )}
                                />
                            </div>
                            <div className="invoice-info-input-field">
                                <Autocomplete
                                    fullWidth
                                    id="state"
                                    name="state"
                                    options={allStates}
                                    disabled={!formik.values.isEditable}
                                    value={formik.values.state}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) =>
                                        formik.setFieldValue('state', newValue)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="State"
                                            error={
                                                formik.touched.state && Boolean(formik.errors.state)
                                            }
                                            helperText={formik.touched.state && formik.errors.state}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <TextField
                                    required
                                    fullWidth
                                    label="Pin Code"
                                    name="pinCode"
                                    id="pinCode"
                                    value={formik.values.pinCode}
                                    disabled={!formik.values.isEditable}
                                    onChange={(e) =>
                                        formik.setFieldValue('pinCode', e.target.value)
                                    }
                                    error={formik.touched.pinCode && Boolean(formik.errors.pinCode)}
                                    helperText={formik.touched.pinCode && formik.errors.pinCode}
                                    slotProps={{ htmlInput: { maxLength: 6 } }}
                                />
                            </div>
                            <div className="invoice-info-input-field">
                                <Autocomplete
                                    fullWidth
                                    id="country"
                                    name="country"
                                    options={countries}
                                    disabled={true}
                                    value={formik.values.country}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) =>
                                        formik.setFieldValue('country', newValue)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country"
                                            error={
                                                formik.touched.country &&
                                                Boolean(formik.errors.country)
                                            }
                                            helperText={
                                                formik.touched.country && formik.errors.country
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default AddressInfo;
